import { useAtom, useAtomValue } from 'jotai';
import { useCallback, useEffect, useMemo, useRef } from 'react';

import { PushDrawer, PushDrawerProps } from '../components/PushDrawer';
import { SettingsDrawerWrapper as SettingsDrawer } from '../components/settings-drawer/SettingsDrawerWrapper';
import { logger } from '../datadog/logger';
import { useDrawerButtonRefs } from '../hooks/useDrawerButtonRefs';
import { useTheme } from '../hooks/useTheme';
import { currentConversationSidAtom } from '../session';
import { Theme } from '../state/theme';
import { isVirtualBackgroundSupportedAtom } from '../video-sdk/core/atoms';
import { ChatDrawerWrapper as ChatDrawer } from './chat/ChatDrawerWrapper';
import { IntentionsDrawer } from './intentions/IntentionsDrawer';
import { ClientMeasuresDrawerWrapper as ClientMeasuresDrawer } from './measures/ClientMeasuresDrawerWrapper';
import { ReflectionTopicsDrawerWrapper as ReflectionTopicsDrawer } from './reflections/ReflectionTopicsDrawerWrapper';
import { getSupportedTabs } from './settingsTabMap';
import { VisitorDrawerState, activeConversationSidAtom, visitorDrawerStateAtom } from './state';
import { useTrackVisitorDrawerEvents } from './useTrackVisitorDrawerEvents';

function DrawerContent({
  drawerState,
  isVirtualBackgroundSupported,
}: {
  drawerState: VisitorDrawerState | null;
  isVirtualBackgroundSupported?: boolean;
}) {
  const supportedTabs = useMemo(
    () => getSupportedTabs({ isVirtualBackgroundSupported }),
    [isVirtualBackgroundSupported],
  );

  switch (drawerState) {
    case VisitorDrawerState.CHAT:
      return <ChatDrawer />;
    case VisitorDrawerState.INTENTIONS:
      return <IntentionsDrawer />;
    case VisitorDrawerState.PENDING_FORMS:
      return <ClientMeasuresDrawer />;
    case VisitorDrawerState.REFLECTION_TOPICS:
      return <ReflectionTopicsDrawer />;
    case VisitorDrawerState.SETTINGS:
      return <SettingsDrawer tabMap={supportedTabs} />;
    default:
      return null;
  }
}

function logPushDrawerError(error: Error) {
  logger.error('The push drawer crashed after encountering an unexpected error; displaying fallback', error);
}

export function PushDrawerWrapper({
  className,
  id,
  onCloseDrawer: onCloseDrawerProp,
}: Pick<PushDrawerProps, 'className' | 'id'> & {
  onCloseDrawer?: () => void;
}) {
  const { buttonRefs: openButtonRefs } = useDrawerButtonRefs();
  const { theme } = useTheme();
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const [visitorDrawerState, setVisitorDrawerState] = useAtom(visitorDrawerStateAtom);
  const [currentConversationSid, setCurrentConversationSid] = useAtom(currentConversationSidAtom);
  const activeConversationSid = useAtomValue(activeConversationSidAtom);
  const isVirtualBackgroundSupported = useAtomValue(isVirtualBackgroundSupportedAtom);
  useTrackVisitorDrawerEvents();

  const onCloseDrawer = useCallback((): void => {
    setVisitorDrawerState(null);
    if (currentConversationSid) {
      setCurrentConversationSid(undefined);
    }
    onCloseDrawerProp?.();
    if (visitorDrawerState) {
      // On drawer close, try to refocus the button that would normally open
      // whatever `drawerState` was active at the time of closing.
      openButtonRefs.current?.get(visitorDrawerState)?.current?.focus();
    }
  }, [
    currentConversationSid,
    setCurrentConversationSid,
    setVisitorDrawerState,
    visitorDrawerState,
    openButtonRefs,
    onCloseDrawerProp,
  ]);

  // On drawer state change, refocus the "close" button.
  useEffect(
    function focusCloseButtonOnDrawerChange() {
      if (visitorDrawerState) {
        closeButtonRef.current?.focus();
      }
    },
    [visitorDrawerState],
  );

  useEffect(
    function syncConversationSidOnChatOpen() {
      if (visitorDrawerState === VisitorDrawerState.CHAT && !currentConversationSid) {
        setCurrentConversationSid(activeConversationSid);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentConversationSid, visitorDrawerState, activeConversationSid],
  );

  return (
    <PushDrawer
      isDarkTheme={theme === Theme.DARK}
      onError={logPushDrawerError}
      id={id}
      isOpen={!!visitorDrawerState}
      onClose={onCloseDrawer}
      className={className}
      closeButtonRef={closeButtonRef}
    >
      <DrawerContent drawerState={visitorDrawerState} isVirtualBackgroundSupported={isVirtualBackgroundSupported} />
    </PushDrawer>
  );
}
